"use client";

import Link from "next/link";
import type { ChoghadiyaResponse } from "@/lib/api/panchang";
import { ChoghadiyaSummary } from "./ChoghadiyaSummary";
import { ChoghadiyaLegend } from "./ChoghadiyaLegend";
import { ChoghadiyaTable } from "./ChoghadiyaTable";

interface ChoghadiyaResultProps {
  result: ChoghadiyaResponse;
}

export function ChoghadiyaResult({ result }: ChoghadiyaResultProps) {
  return (
    <div className="space-y-6">
      <ChoghadiyaSummary result={result} />

      <ChoghadiyaLegend />

      <ChoghadiyaTable
        title="Day Choghadiya"
        titleHi="दिन का चौघड़िया"
        periods={result.day_choghadiya}
        timeRange={`${result.sunrise} - ${result.sunset}`}
      />

      <ChoghadiyaTable
        title="Night Choghadiya"
        titleHi="रात का चौघड़िया"
        periods={result.night_choghadiya}
        timeRange={`${result.sunset} - ${result.next_sunrise}`}
      />

      {/* Vaar Vela / Kaal Vela / Kaal Ratri */}
      {result.vaar_vela && (
        <div className="card-sacred p-4 space-y-3">
          <h3
            className="text-[15px] font-bold"
            style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
          >
            Inauspicious Periods
            <span
              className="block text-[12px] font-normal mt-0.5"
              style={{ color: "var(--ish-text-muted)" }}
            >
              वार वेला · काल वेला · काल रात्रि
            </span>
          </h3>
          <p className="text-[12px]" style={{ color: "var(--ish-text-muted)" }}>
            Avoid starting new work during these periods. Tradition considers them unfavourable for
            auspicious activities.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { label: "Vaar Vela", labelHi: "वार वेला", ...result.vaar_vela },
              { label: "Kaal Vela", labelHi: "काल वेला", ...result.kaal_vela },
              { label: "Kaal Ratri", labelHi: "काल रात्रि", ...result.kaal_ratri },
            ].map(({ label, labelHi, start_time, end_time }) => (
              <div
                key={label}
                className="rounded-lg p-3 text-center"
                style={{
                  background: "var(--color-error-bg, #fef2f2)",
                  border: "1px solid var(--color-error-border, #fecaca)",
                }}
              >
                <p
                  className="text-[11px] font-semibold uppercase tracking-wide mb-0.5"
                  style={{ color: "var(--color-error, #dc2626)" }}
                >
                  {label}
                </p>
                <p
                  className="text-[10px] mb-1"
                  style={{ color: "var(--color-error, #dc2626)", opacity: 0.7 }}
                >
                  {labelHi}
                </p>
                <p className="text-[13px] font-bold" style={{ color: "var(--ish-dark)" }}>
                  {start_time} – {end_time}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="card-sacred p-6 text-center bg-black/5">
        <p className="text-[15px] font-semibold mb-2 text-ish-dark">Get Personal Timing Guidance</p>
        <p className="text-[13px] mb-4 text-ish-text-muted">
          Check your kundali before choosing a muhurat, sankalp, or paid timing report.
        </p>
        <Link
          href="/free-kundali"
          className="btn-sacred inline-block font-semibold py-3 px-6 rounded-lg text-[14px]"
        >
          Generate Free Kundali &rarr;
        </Link>
      </div>
    </div>
  );
}
